import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

export default function Shopping() {
  const { t } = useLang();
  const [lists, setLists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [newListName, setNewListName] = useState("");
  const [newItem, setNewItem] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/shopping/lists");
    setLists(data.lists);
    setActiveId((cur) => (cur ? cur : data.lists[0]?.id ?? null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = useMemo(
    () => lists.find((l) => l.id === activeId) || null,
    [lists, activeId]
  );

  const createList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    try {
      const { data } = await api.post("/shopping/lists", { name: newListName });
      setNewListName("");
      setActiveId(data.list.id);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.trim() || !activeId) return;
    try {
      await api.post("/shopping/items", { title: newItem, list_id: activeId });
      setNewItem("");
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const toggle = async (id) => {
    await api.post(`/shopping/items/${id}/toggle`);
    load();
  };

  const removeItem = async (id) => {
    await api.delete(`/shopping/items/${id}`);
    load();
  };

  const deleteList = async (id) => {
    await api.delete(`/shopping/lists/${id}`);
    setActiveId(null);
    load();
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-6xl">
      <p className="label-cap">— {t("app.sidebar2.shopping")}</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="shopping-title">
        {t("app.shoppingTitle")}
      </h1>
      <p className="font-display-italic text-[var(--ink-soft)] mt-3 max-w-3xl">{t("app.shoppingSub")}</p>

      <div className="grid md:grid-cols-12 gap-12 mt-12">
        <aside className="md:col-span-4">
          <form onSubmit={createList} className="flex gap-3 mb-6" data-testid="new-list-form">
            <input
              className="line-input flex-1"
              placeholder={t("app.shoppingListName")}
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              data-testid="new-list-input"
            />
            <button type="submit" className="btn-outline" data-testid="new-list-submit">
              +
            </button>
          </form>
          {lists.length === 0 && (
            <p className="font-display-italic text-[var(--ink-soft)]" data-testid="shopping-empty-lists">
              {t("app.shoppingEmptyLists")}
            </p>
          )}
          <ul className="space-y-px bg-[var(--border)]">
            {lists.map((l) => (
              <li
                key={l.id}
                onClick={() => setActiveId(l.id)}
                className={`p-4 cursor-pointer bg-[var(--bg)] flex items-center justify-between ${l.id === activeId ? "border-l-2 border-[var(--oxblood)]" : ""}`}
                data-testid={`shopping-list-${l.id}`}
              >
                <div>
                  <p className="font-display text-lg">{l.name}</p>
                  <p className="text-xs text-[var(--ink-faint)] tracking-monocle uppercase mt-1">
                    {l.pending} {t("app.shoppingPending")}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteList(l.id);
                  }}
                  className="text-xs text-[var(--ink-faint)] hover:text-[var(--error)]"
                  data-testid={`shopping-list-delete-${l.id}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="md:col-span-8">
          {active && (
            <>
              <div className="flex items-center justify-between">
                <h2 className="font-display text-3xl font-light">{active.name}</h2>
                <span className="label-cap text-[var(--ink-soft)]">{active.items.length} items</span>
              </div>
              <form onSubmit={addItem} className="mt-6 flex gap-3" data-testid="add-item-form">
                <input
                  className="line-input flex-1"
                  placeholder={t("app.shoppingAddItem")}
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  data-testid="add-item-input"
                />
                <button type="submit" className="btn-primary" data-testid="add-item-submit">
                  +
                </button>
              </form>
              <ul className="mt-8 divide-y divide-[var(--border)] border-y border-[var(--border)]" data-testid="shopping-items">
                {active.items.map((it) => (
                  <li key={it.id} className="py-4 flex items-center justify-between" data-testid={`shopping-item-${it.id}`}>
                    <button
                      onClick={() => toggle(it.id)}
                      className="flex items-center gap-4 flex-1 text-left"
                      data-testid={`shopping-item-toggle-${it.id}`}
                    >
                      <span
                        className={`w-4 h-4 border ${it.done ? "bg-[var(--ink)] border-[var(--ink)]" : "border-[var(--ink-soft)]"} flex items-center justify-center`}
                      >
                        {it.done && <span className="text-[var(--bg)] text-xs">✓</span>}
                      </span>
                      <span className={`font-display text-lg ${it.done ? "line-through text-[var(--ink-faint)]" : ""}`}>
                        {it.title}
                      </span>
                      {it.from_recipe && (
                        <span className="text-xs text-[var(--ink-faint)] italic">· {it.from_recipe}</span>
                      )}
                    </button>
                    <button
                      onClick={() => removeItem(it.id)}
                      className="text-xs text-[var(--ink-faint)] hover:text-[var(--error)] ml-3"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
      {error && <p className="text-[var(--error)] text-sm mt-6">{error}</p>}
    </div>
  );
}
